import { useState, useMemo, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useModulos } from "@/hooks/use-modulos";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  Search,
  Car,
  Package,
  CheckCircle2,
  AlertCircle,
  Building2,
  Lock,
  Send,
  Clock,
  Plus,
  Trash2,
  Star,
} from "lucide-react";
import { api } from "@shared/routes";
import { VEICULO_TIPO_LABELS } from "@/lib/constants";
import { supabase } from "@/lib/supabase";
import { FotosVeiculo, buscarFotosVeiculo, chaveFotosVeiculo, useFotosVeiculo } from "@/components/veiculos/FotosVeiculo";
import { posicoesFaltantes } from "@shared/veiculo-fotos";
import {
  AVISO_ATRIBUICAO_PELA_VISTORIADORA,
  AVISO_FOTOS_PARA_TECNICO,
  TEXTO_ENVIAR_ANALISE,
  TEXTO_ENVIAR_TECNICO,
  solicitarEnvioTecnico,
  type ResultadoEnvioTecnico,
} from "@/components/laudos/EnvioTecnicoVistoria";
import type { Veiculo, Peca, Empresa, Laudo } from "@shared/schema";
import { creditosLivres } from "@shared/credito-saldo";
import { erroPecasAvulsas } from "@shared/peca-avulsa";
import { LerQrCartelaButton } from "@/components/laudos/LerQrCartelaButton";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function SolicitarLaudoDialog({ open, onClose }: Props) {
  const { profile, session } = useAuth();
  const { modulos } = useModulos();
  const { toast } = useToast();

  const isParceiro = profile?.roles?.includes("parceiro_negocios") ?? false;
  // Envio direto ao Técnico de Vistoria: só o Parceiro, e só quando a vistoriadora dele tem exatamente 1 TV.
  const podeEscolherTecnico = isParceiro && modulos.tvUnico;

  // Step 1: Parceiro (apenas vistoriadora) | Step 2: Veículo | Step 3: Confirmar
  const [step, setStep] = useState<1 | 2 | 3>(isParceiro ? 2 : 1);

  // Etapa final: fotos do veículo do laudo recém-criado (só com o módulo Detran e fotos faltando)
  const [laudoCriado, setLaudoCriado] = useState<{ veiculoId: string; pedidoTv: boolean } | null>(null);

  // Step 1
  const [parceiroSearch, setParceiroSearch] = useState("");
  const [selectedParceiro, setSelectedParceiro] = useState<Empresa | null>(
    null,
  );

  // Parceiro solicita laudo pela própria empresa — pula step 1
  useEffect(() => {
    // a etapa de fotos não sobrevive a fechar/abrir, mesmo que a consulta termine depois do fechamento
    setLaudoCriado(null);
    if (open && isParceiro && profile?.empresa) {
      setSelectedParceiro(profile.empresa as Empresa);
      setStep(2);
    }
    if (!open) {
      setStep(isParceiro ? 2 : 1);
    }
  }, [open]);

  // Step 2
  const [veiculoSearch, setVeiculoSearch] = useState("");
  const [tipoFilter, setTipoFilter] = useState("all");
  const [selectedVeiculo, setSelectedVeiculo] = useState<Veiculo | null>(null);

  // Step 3
  const [cartela, setCartela] = useState("");
  const [cartelaError, setCartelaError] = useState<string | null>(null);
  const [cartelaValida, setCartelaValida] = useState<boolean | null>(null);
  const [cartelaVerificando, setCartelaVerificando] = useState(false);
  const [pecasAvulsas, setPecasAvulsas] = useState<{ id: string; nome: string; numeroEtiqueta: string }[]>([]);
  const [novaAvulsa, setNovaAvulsa] = useState("");
  const [numeroEtiquetaAvulsa, setNumeroEtiquetaAvulsa] = useState("");

  // Aviso do passo 3: com o Detran ligado e o veículo sem as 4 fotos, o técnico só recebe o laudo quando elas chegarem.
  const { data: fotosDoVeiculo } = useFotosVeiculo(selectedVeiculo?.id, step === 3 && podeEscolherTecnico && modulos.detranVeiculoFoto);
  const faltamFotos = !!fotosDoVeiculo && posicoesFaltantes(fotosDoVeiculo).length > 0;

  const authFetch = (url: string) =>
    fetch(url, {
      headers: { Authorization: `Bearer ${session?.access_token}` },
    }).then((r) => r.json());

  // Verificar status de bloqueio — apenas para parceiros
  const { data: statusLimite, isLoading: statusLoading } = useQuery<{
    bloqueado: boolean;
    limite: number;
    laudosMes: number;
    temAutorizacao: boolean;
    solicitacaoPendente: boolean;
  }>({
    queryKey: ["solicitacoes-laudo-status"],
    queryFn: () => authFetch("/api/solicitacoes-laudo/status"),
    enabled: open && isParceiro,
    // ponytail: sem staleTime — precisa buscar fresco toda vez que o dialog abre,
    // porque uma autorização pode ter sido aprovada por outro usuário/aba entre uma
    // abertura e outra (era 30_000, causava reabrir o dialog mostrando "peça
    // autorização" mesmo com uma solicitação já aprovada no banco).
  });

  const bloqueado = isParceiro && (statusLimite?.bloqueado ?? false);
  const temAutorizacao = statusLimite?.temAutorizacao ?? false;
  const solicitacaoPendente = statusLimite?.solicitacaoPendente ?? false;

  // Mutação para solicitar autorização
  const [solicitacaoEnviada, setSolicitacaoEnviada] = useState(false);
  const solicitarMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/solicitacoes-laudo", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao enviar solicitação");
      }
      return res.json();
    },
    onSuccess: () => {
      setSolicitacaoEnviada(true);
      queryClient.invalidateQueries({
        queryKey: ["solicitacoes-laudo-status"],
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Erro",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  // Buscar parceiros vinculados à vistoriadora
  const { data: empresas, isLoading: empresasLoading } = useQuery<Empresa[]>({
    queryKey: [api.empresas.list.path],
    queryFn: () => authFetch(api.empresas.list.path),
    enabled: open && !!session,
  });

  const parceiros = useMemo(() => {
    if (!empresas) return [];
    return empresas.filter((e) => e.tipo === "PARCEIRO_DE_NEGOCIOS");
  }, [empresas]);

  const parceirosFiltrados = useMemo(() => {
    const q = parceiroSearch.toLowerCase();
    if (!q) return parceiros;
    return parceiros.filter(
      (p) =>
        p.razaoSocial.toLowerCase().includes(q) ||
        (p.nomeFantasia ?? "").toLowerCase().includes(q) ||
        p.uf.toLowerCase().includes(q),
    );
  }, [parceiros, parceiroSearch]);

  // Buscar créditos do parceiro — só quando o módulo Créditos Pré-Pagos estiver ativo
  const { data: creditosParceiro, isLoading: creditosLoading } = useQuery<
    Record<string, number>
  >({
    queryKey: ["creditos_balance_parceiro", selectedParceiro?.id],
    queryFn: async () => {
      // Saldo utilizável (lotes do ambiente atual), não o agregado creditos_balance, que mistura teste e Produção.
      const data: { tipo_veiculo: string; disponivel: number; reservado: number }[] = await authFetch(
        `/api/creditos/saldo-utilizavel?parceiroId=${selectedParceiro!.id}`,
      );
      const map: Record<string, number> = {};
      for (const row of Array.isArray(data) ? data : []) {
        map[row.tipo_veiculo] =
          (map[row.tipo_veiculo] ?? 0) + creditosLivres(row);
      }
      return map;
    },
    enabled: !!selectedParceiro && modulos.creditosPrePagos,
  });

  // Buscar veículos do parceiro selecionado
  const { data: veiculos, isLoading: veiculosLoading } = useQuery<Veiculo[]>({
    queryKey: [api.veiculos.list.path, selectedParceiro?.id, "disponiveis"],
    queryFn: () =>
      authFetch(
        `${api.veiculos.list.path}?parceiroId=${selectedParceiro!.id}&apenasDisponiveis=true`,
      ),
    enabled: step >= 2 && !!selectedParceiro,
  });

  const veiculosFiltrados = useMemo(() => {
    if (!veiculos) return [];
    return veiculos.filter((v) => {
      const matchTipo = tipoFilter === "all" || v.tipo === tipoFilter;
      const q = veiculoSearch.toLowerCase();
      const matchSearch =
        !q ||
        v.placa.toLowerCase().includes(q) ||
        v.marca.toLowerCase().includes(q) ||
        v.modelo.toLowerCase().includes(q) ||
        v.chassi.toLowerCase().includes(q);
      return matchTipo && matchSearch;
    });
  }, [veiculos, veiculoSearch, tipoFilter]);

  const uf = selectedParceiro?.uf ?? "SP";

  // Buscar peças do catálogo para o tipo do veículo selecionado
  const { data: pecasCatalogo, isLoading: pecasLoading } = useQuery<Peca[]>({
    queryKey: [api.pecas.list.path, selectedVeiculo?.tipo, uf],
    queryFn: () =>
      authFetch(
        `${api.pecas.list.path}?tipoVeiculo=${selectedVeiculo?.tipo}&uf=${uf}`,
      ),
    enabled: step === 3 && !!selectedVeiculo,
  });

  const pecasOrdenadas = useMemo(() => {
    if (!pecasCatalogo) return [];
    return pecasCatalogo
      .filter((p) => p.ufs?.includes(uf) && p.ativo)
      .sort((a, b) => {
        const seqA = (a.ufSequencias as Record<string, number>)?.[uf] ?? 999;
        const seqB = (b.ufSequencias as Record<string, number>)?.[uf] ?? 999;
        return seqA - seqB;
      });
  }, [pecasCatalogo, uf]);

  const handleCartelaChange = async (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    setCartela(digits);
    setCartelaError(null);
    setCartelaValida(null);

    if (digits.length === 10) {
      setCartelaVerificando(true);
      try {
        const res = await fetch(`/api/laudos/cartela/${digits}/disponivel`, {
          headers: { Authorization: `Bearer ${session?.access_token}` },
        });
        if (!res.ok) throw new Error(`Erro ${res.status}`);
        const { disponivel } = (await res.json()) as { disponivel: boolean };
        setCartelaValida(disponivel);
        if (!disponivel) setCartelaError("Cartela já está em uso em outro laudo");
      } catch {
        setCartelaValida(null);
      } finally {
        setCartelaVerificando(false);
      }
    }
  };

  const createMutation = useMutation({
    mutationFn: async (enviarAoTecnico: boolean) => {
      if (!selectedVeiculo) throw new Error("Veículo não selecionado");

      const pecasCatalogoPay = pecasOrdenadas.map((p, idx) => ({
        catalogoPecaId: p.id,
        sequencia: (p.ufSequencias as Record<string, number>)?.[uf] ?? idx + 1,
        etiqueta:
          cartela.length === 10
            ? buildEtiqueta(p, selectedVeiculo, cartela, uf)
            : "",
        uf,
        nome: p.nome,
        tipoVeiculo: p.tipoVeiculo,
      }));

      const baseSeq = pecasCatalogoPay.length;
      const pecasAvulsasPay = pecasAvulsas.map((a, idx) => ({
        catalogoPecaId: null,
        sequencia: baseSeq + idx + 1,
        etiqueta: `${uf}5${a.numeroEtiqueta}`,
        uf,
        nome: a.nome,
        tipoVeiculo: "avulsa",
      }));

      const erroAvulsas = erroPecasAvulsas(pecasAvulsasPay);
      if (erroAvulsas) throw new Error(erroAvulsas);

      const res = await fetch("/api/laudos", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          veiculoId: selectedVeiculo.id,
          parceiroId: selectedVeiculo.parceiroId,
          cartela: cartela.length === 10 ? cartela : undefined,
          pecas: [...pecasCatalogoPay, ...pecasAvulsasPay],
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null); // 502/HTML do proxy: sem corpo JSON o erro continua em português
        throw new Error(err?.message || "Erro ao criar laudo");
      }
      const laudo = await res.json();
      // Falha no pedido não pode desfazer nem repetir o laudo: vira aviso ao usuário (nunca lança).
      const envio = enviarAoTecnico ? await solicitarEnvioTecnico(laudo?.id, session?.access_token) : null;
      return { laudo, envio };
    },
    onSuccess: async ({ laudo, envio }: { laudo: Pick<Laudo, "veiculoId" | "moduloDetranVeiculoFoto"> | null; envio: ResultadoEnvioTecnico | null }) => {
      queryClient.invalidateQueries({ queryKey: [api.laudos.list.path] });
      queryClient.invalidateQueries({ queryKey: ["creditos_balance"] });
      queryClient.invalidateQueries({ queryKey: ["creditos_balance_parceiro"] });
      const pedidoTv = envio?.ok === true && !envio.tecnicoAtribuido;
      toast({
        title: "Laudo solicitado com sucesso!",
        description: envio?.ok
          ? envio.tecnicoAtribuido
            ? "O laudo foi enviado ao Técnico de Vistoria."
            : laudo?.moduloDetranVeiculoFoto ? AVISO_FOTOS_PARA_TECNICO : AVISO_ATRIBUICAO_PELA_VISTORIADORA
          : undefined,
      });
      if (envio && !envio.ok) {
        toast({
          title: "Não foi possível enviar ao Técnico de Vistoria",
          description: `${envio.message} Abra o laudo para tentar de novo.`,
          variant: "destructive",
        });
      }
      // As fotos não impedem solicitar: a etapa só abre se o banco ligou o módulo neste laudo e ainda faltam fotos.
      const veiculoId = laudo?.veiculoId; // resposta vazia não pode virar erro depois do laudo já criado
      if (laudo?.moduloDetranVeiculoFoto && veiculoId) {
        const fotos = await queryClient
          .fetchQuery({
            queryKey: chaveFotosVeiculo(veiculoId),
            queryFn: () => buscarFotosVeiculo(veiculoId, session?.access_token),
            staleTime: 0,
          })
          .catch(() => null); // falha na consulta: a etapa abre e mostra o erro com "Tentar de novo"
        if (!fotos || posicoesFaltantes(fotos).length > 0) {
          setLaudoCriado({ veiculoId, pedidoTv });
          return;
        }
      }
      handleClose();
    },
    onError: (err: Error) => {
      toast({
        title: "Erro",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const handleClose = () => {
    setLaudoCriado(null);
    setStep(isParceiro ? 2 : 1);
    if (!isParceiro) setSelectedParceiro(null);
    setParceiroSearch("");
    setSelectedVeiculo(null);
    setVeiculoSearch("");
    setTipoFilter("all");
    setCartela("");
    setCartelaError(null);
    setCartelaValida(null);
    setSolicitacaoEnviada(false);
    setPecasAvulsas([]);
    setNovaAvulsa("");
    setNumeroEtiquetaAvulsa("");
    onClose();
  };

  const adicionarAvulsa = () => {
    const nome = novaAvulsa.trim().toUpperCase();
    if (!nome || !/^[0-9]{1,13}$/.test(numeroEtiquetaAvulsa)) return;
    if (pecasAvulsas.some((p) => p.numeroEtiqueta === numeroEtiquetaAvulsa)) {
      toast({ title: "Etiqueta repetida", description: "Informe uma etiqueta diferente para cada peça especial.", variant: "destructive" });
      return;
    }
    setPecasAvulsas((prev) => [...prev, { id: crypto.randomUUID(), nome, numeroEtiqueta: numeroEtiquetaAvulsa }]);
    setNovaAvulsa("");
    setNumeroEtiquetaAvulsa("");
  };

  const removerAvulsa = (id: string) =>
    setPecasAvulsas((prev) => prev.filter((a) => a.id !== id));

  const stepTitles = {
    1: "Solicitar Laudo — Selecionar Cliente",
    2: "Solicitar Laudo — Selecionar Veículo",
    3: "Solicitar Laudo — Confirmar",
  };

  // Fora do gating de "Limite Atingido": uma nova consulta do status do mês não pode trocar esta etapa.
  if (laudoCriado) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-lg">
              Solicitar Laudo — Fotos do veículo
            </DialogTitle>
            <DialogDescription>
              {laudoCriado.pedidoTv
                ? `Laudo solicitado e pedido de envio ao Técnico de Vistoria registrado. Envie as fotos do veículo agora ou mais tarde. ${AVISO_FOTOS_PARA_TECNICO}`
                : "Laudo solicitado. Envie as fotos do veículo agora ou mais tarde. O técnico de vistoria só pode ser atribuído com as 4."}
            </DialogDescription>
          </DialogHeader>
          <EtapaFotosVeiculo
            veiculoId={laudoCriado.veiculoId}
            onClose={handleClose}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-lg">
            {isParceiro && !statusLoading && bloqueado && !temAutorizacao
              ? "Limite Atingido"
              : stepTitles[step]}
          </DialogTitle>
        </DialogHeader>

        {/* ── LOADING STATUS ── */}
        {isParceiro && statusLoading && (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        )}

        {/* ── BLOQUEADO: sem autorização ── */}
        {isParceiro && !statusLoading && bloqueado && !temAutorizacao && (
          <div className="flex flex-col items-center gap-5 py-4">
            <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center">
              <Lock className="w-7 h-7 text-red-500" />
            </div>
            <div className="text-center space-y-1">
              <p className="font-semibold text-slate-800">
                Limite mensal atingido
              </p>
              <p className="text-sm text-muted-foreground">
                Você atingiu o limite de <strong>{statusLimite?.limite}</strong>{" "}
                laudo{(statusLimite?.limite ?? 1) > 1 ? "s" : ""} por
                associado/mês ({statusLimite?.laudosMes} realizado
                {(statusLimite?.laudosMes ?? 0) !== 1 ? "s" : ""} este mês).
              </p>
            </div>

            {solicitacaoEnviada || solicitacaoPendente ? (
              <div className="w-full flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <Clock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    Solicitação pendente
                  </p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    Sua solicitação de autorização foi enviada. Aguarde a
                    aprovação do Administrador ou Responsável Técnico.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground text-center max-w-sm">
                  Para solicitar um laudo adicional, envie uma solicitação de
                  autorização para o Administrador ou Responsável Técnico.
                </p>
                <Button
                  onClick={() => solicitarMutation.mutate()}
                  disabled={solicitarMutation.isPending}
                  className="w-full max-w-xs"
                >
                  {solicitarMutation.isPending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Solicitar Autorização
                </Button>
              </>
            )}

            <Button
              variant="outline"
              className="w-full max-w-xs"
              onClick={handleClose}
            >
              Fechar
            </Button>
          </div>
        )}

        {/* ── BANNER: tem autorização aprovada ── */}
        {isParceiro && !statusLoading && bloqueado && temAutorizacao && (
          <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-xl mb-2">
            <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
            <p className="text-sm text-green-800">
              <strong>Autorização aprovada.</strong> Este laudo utilizará sua
              autorização de limite adicional.
            </p>
          </div>
        )}

        {/* ── STEPS NORMAIS (só quando não bloqueado, ou bloqueado mas com autorização) ── */}
        {(!isParceiro || !statusLoading) && (!bloqueado || temAutorizacao) && (
          <>
            {/* ── STEP 1: Selecionar Parceiro ── */}
            {step === 1 && (
              <div className="flex flex-col gap-4 overflow-hidden">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    className="pl-9"
                    placeholder="Buscar por nome, fantasia ou UF..."
                    value={parceiroSearch}
                    onChange={(e) => setParceiroSearch(e.target.value)}
                  />
                </div>

                <ScrollArea className="flex-1 max-h-[50vh]">
                  {empresasLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-5 h-5 animate-spin text-primary" />
                    </div>
                  ) : parceirosFiltrados.length === 0 ? (
                    <div className="text-center py-10 text-muted-foreground">
                      <Building2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p>Nenhum cliente parceiro encontrado</p>
                    </div>
                  ) : (
                    <div className="space-y-2 pr-2">
                      {parceirosFiltrados.map((p) => (
                        <div
                          key={p.id}
                          onClick={() => setSelectedParceiro(p)}
                          className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${
                            selectedParceiro?.id === p.id
                              ? "border-primary bg-primary/5"
                              : "border-slate-200 hover:border-slate-300"
                          }`}
                        >
                          <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                            <Building2 className="w-4 h-4 text-slate-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-slate-800 text-sm truncate">
                              {p.nomeFantasia || p.razaoSocial}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {p.razaoSocial} · {p.uf}
                            </p>
                          </div>
                          <Badge variant="outline" className="text-xs shrink-0">
                            {p.uf}
                          </Badge>
                          {selectedParceiro?.id === p.id && (
                            <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>

                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button variant="outline" onClick={handleClose}>
                    Cancelar
                  </Button>
                  <Button
                    disabled={!selectedParceiro}
                    onClick={() => setStep(2)}
                  >
                    Próximo
                  </Button>
                </div>
              </div>
            )}

            {/* ── STEP 2: Selecionar Veículo ── */}
            {step === 2 && selectedParceiro && (
              <div className="flex flex-col gap-4 overflow-hidden">
                {/* Parceiro selecionado */}
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border">
                  <Building2 className="w-5 h-5 text-slate-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">
                      {selectedParceiro.nomeFantasia ||
                        selectedParceiro.razaoSocial}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {selectedParceiro.uf}
                    </p>
                  </div>
                </div>

                {/* Filtros */}
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      className="pl-9"
                      placeholder="Buscar por placa, marca, modelo, chassi..."
                      value={veiculoSearch}
                      onChange={(e) => setVeiculoSearch(e.target.value)}
                    />
                  </div>
                  <Select value={tipoFilter} onValueChange={setTipoFilter}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os tipos</SelectItem>
                      {Object.entries(VEICULO_TIPO_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <ScrollArea className="flex-1 max-h-[40vh]">
                  {veiculosLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-5 h-5 animate-spin text-primary" />
                    </div>
                  ) : veiculosFiltrados.length === 0 ? (
                    <div className="text-center py-10 text-muted-foreground">
                      <Car className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p>Nenhum veículo encontrado para este cliente</p>
                    </div>
                  ) : (
                    <div className="space-y-2 pr-2">
                      {veiculosFiltrados.map((v) => {
                        const semCredito =
                          modulos.creditosPrePagos &&
                          creditosParceiro !== undefined &&
                          (creditosParceiro[v.tipo] ?? 0) <= 0;
                        return (
                          <div
                            key={v.id}
                            onClick={() => !semCredito && setSelectedVeiculo(v)}
                            className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-colors ${
                              semCredito
                                ? "opacity-50 cursor-not-allowed border-slate-100"
                                : selectedVeiculo?.id === v.id
                                  ? "border-primary bg-primary/5 cursor-pointer"
                                  : "border-slate-200 hover:border-slate-300 cursor-pointer"
                            }`}
                          >
                            <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                              <Car className="w-4 h-4 text-slate-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-slate-800 text-sm">
                                  {v.marca} {v.modelo}
                                </p>
                                <Badge variant="secondary" className="text-xs">
                                  {VEICULO_TIPO_LABELS[v.tipo] ?? v.tipo}
                                </Badge>
                                {modulos.creditosPrePagos &&
                                  creditosParceiro !== undefined &&
                                  (semCredito ? (
                                    <Badge
                                      variant="destructive"
                                      className="text-xs"
                                    >
                                      sem crédito
                                    </Badge>
                                  ) : (
                                    <Badge
                                      variant="outline"
                                      className="text-xs text-emerald-700 border-emerald-300 bg-emerald-50"
                                    >
                                      {creditosParceiro[v.tipo] ?? 0} crédito{(creditosParceiro[v.tipo] ?? 0) !== 1 ? "s" : ""}
                                    </Badge>
                                  ))}
                              </div>
                              <p className="text-xs text-muted-foreground font-mono">
                                {v.placa} · {v.chassi}
                              </p>
                            </div>
                            {!semCredito && selectedVeiculo?.id === v.id && (
                              <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>

                <div className="flex justify-between gap-2 pt-2 border-t">
                  {!isParceiro && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSelectedVeiculo(null);
                        setStep(1);
                      }}
                    >
                      Voltar
                    </Button>
                  )}
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleClose}>
                      Cancelar
                    </Button>
                    <Button
                      disabled={
                        !selectedVeiculo ||
                        (modulos.creditosPrePagos && creditosLoading) ||
                        (modulos.creditosPrePagos &&
                          creditosParceiro !== undefined &&
                          (creditosParceiro[selectedVeiculo?.tipo ?? ""] ??
                            0) <= 0)
                      }
                      onClick={() => setStep(3)}
                    >
                      {creditosLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : null}
                      Próximo
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* ── STEP 3: Confirmar (cartela + peças) ── */}
            {step === 3 && selectedVeiculo && selectedParceiro && (
              <div className="flex-1 flex flex-col overflow-hidden min-h-0">
                <div className="flex-1 overflow-y-auto min-h-0 flex flex-col gap-4 pb-2">
                {/* Resumo: parceiro + veículo */}
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border">
                    <Building2 className="w-4 h-4 text-slate-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">
                        {selectedParceiro.nomeFantasia ||
                          selectedParceiro.razaoSocial}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {selectedParceiro.uf}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border">
                    <Car className="w-4 h-4 text-slate-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm">
                        {selectedVeiculo.marca} {selectedVeiculo.modelo}
                      </p>
                      <p className="text-xs text-muted-foreground font-mono">
                        {selectedVeiculo.placa} ·{" "}
                        {VEICULO_TIPO_LABELS[selectedVeiculo.tipo]}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Cartela (opcional) */}
                <div className="space-y-2">
                  <Label>
                    Número da Cartela{" "}
                    <span className="text-muted-foreground text-xs">
                      (opcional — pode ser inserido depois)
                    </span>
                  </Label>
                  <div className="flex gap-2">
                    <div className="relative flex-1 min-w-0">
                      <Input
                        value={cartela}
                        onChange={(e) => handleCartelaChange(e.target.value)}
                        placeholder="10 dígitos numéricos"
                        inputMode="numeric"
                        maxLength={10}
                        className={
                          cartelaValida === false
                            ? "border-destructive"
                            : cartelaValida
                              ? "border-green-500"
                              : ""
                        }
                      />
                      {cartelaVerificando && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />
                      )}
                      {cartelaValida === true && !cartelaVerificando && (
                        <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                      )}
                      {cartelaValida === false && !cartelaVerificando && (
                        <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-destructive" />
                      )}
                    </div>
                    <LerQrCartelaButton
                      tipoVeiculo={selectedVeiculo?.tipo}
                      onCartela={handleCartelaChange}
                    />
                  </div>
                  {cartelaError && (
                    <p className="text-xs text-destructive">{cartelaError}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {cartela.length}/10 dígitos
                  </p>
                </div>

                {/* Lista de peças do catálogo */}
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Package className="w-4 h-4" />
                    Peças do catálogo ({uf} ·{" "}
                    {VEICULO_TIPO_LABELS[selectedVeiculo.tipo]})
                  </Label>
                  {pecasLoading ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    </div>
                  ) : pecasOrdenadas.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Nenhuma peça encontrada no catálogo para este tipo/UF.
                    </p>
                  ) : (
                    <ScrollArea className="h-44 rounded-lg border bg-slate-50">
                      <div className="p-2 space-y-1">
                        {pecasOrdenadas.map((p) => {
                          const seq =
                            (p.ufSequencias as Record<string, number>)?.[uf] ??
                            0;
                          return (
                            <div
                              key={p.id}
                              className="flex items-center gap-2 px-2 py-1 text-sm"
                            >
                              <span className="text-xs font-mono text-slate-400 w-8 shrink-0">
                                {String(seq).padStart(3, "0")}
                              </span>
                              <span className="text-slate-700">{p.nome}</span>
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  )}
                </div>

                {/* Peças avulsas (especiais) */}
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-purple-500" />
                    Peças Especiais (Avulsas)
                    {pecasAvulsas.length > 0 && (
                      <span className="ml-1 text-xs font-normal text-purple-600 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-full">
                        {pecasAvulsas.length} avulsa{pecasAvulsas.length !== 1 ? "s" : ""}
                      </span>
                    )}
                  </Label>

                  <p className="text-xs text-muted-foreground mb-3">Opcional. Ao adicionar uma peça, informe o número da etiqueta avulsa.</p>
                  <div className="flex flex-wrap items-end gap-2 mb-2">
                    <div className="min-w-0 flex-1 basis-48 space-y-1">
                    <Label htmlFor="nome-peca-avulsa">Nome da peça especial</Label>
                    <Input
                      id="nome-peca-avulsa"
                      value={novaAvulsa}
                      onChange={(e) => setNovaAvulsa(e.target.value.toUpperCase())}
                      onKeyDown={(e) => e.key === "Enter" && adicionarAvulsa()}
                      placeholder="Nome da peça especial..."
                      className="flex-1 text-sm"
                    />
                    </div>
                    <div className="min-w-0 flex-1 basis-40 space-y-1">
                      <Label htmlFor="numero-etiqueta-avulsa">Número da etiqueta avulsa</Label>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold shrink-0" aria-label="Prefixo automático da etiqueta">{uf}5</span>
                        <Input id="numero-etiqueta-avulsa" value={numeroEtiquetaAvulsa}
                          onChange={(event) => setNumeroEtiquetaAvulsa(event.target.value.replace(/\D/g, "").slice(0, 13))}
                          onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); adicionarAvulsa(); } }}
                          inputMode="numeric" maxLength={13} placeholder="Somente números" className="min-w-0 text-sm" />
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={adicionarAvulsa}
                      disabled={!novaAvulsa.trim() || !/^[0-9]{1,13}$/.test(numeroEtiquetaAvulsa)}
                      className="shrink-0 gap-1 border-purple-300 text-purple-700 hover:bg-purple-50"
                    >
                      <Plus className="w-4 h-4" />
                      Adicionar
                    </Button>
                  </div>

                  {pecasAvulsas.length > 0 && (
                    <div className="rounded-lg border border-purple-200 bg-purple-50/50 divide-y divide-purple-100">
                      {pecasAvulsas.map((a, idx) => (
                        <div key={a.id} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                          <Star className="w-3 h-3 text-purple-400 shrink-0" />
                          <span className="text-xs font-mono text-purple-400 w-8 shrink-0">
                            {String(pecasOrdenadas.length + idx + 1).padStart(3, "0")}
                          </span>
                          <span className="flex-1 min-w-0 text-purple-900">{a.nome}<span className="block text-xs text-muted-foreground break-all">Etiqueta: {uf}5{a.numeroEtiqueta}</span></span>
                          <button
                            type="button"
                            onClick={() => removerAvulsa(a.id)}
                            className="text-purple-400 hover:text-red-500 transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                </div>

                {podeEscolherTecnico && modulos.detranVeiculoFoto && faltamFotos && (
                  <p className="shrink-0 pt-2 text-xs text-slate-500">{AVISO_FOTOS_PARA_TECNICO}</p>
                )}
                <div className="flex flex-wrap justify-between gap-2 pt-2 border-t mt-2 shrink-0">
                  <Button variant="outline" onClick={() => setStep(2)}>
                    Voltar
                  </Button>
                  <div className="flex flex-wrap justify-end gap-2">
                    <Button variant="outline" onClick={handleClose}>
                      Cancelar
                    </Button>
                    <Button
                      disabled={
                        createMutation.isPending ||
                        cartelaValida === false ||
                        pecasLoading
                      }
                      onClick={() => createMutation.mutate(false)}
                    >
                      {createMutation.isPending && createMutation.variables !== true && (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      )}
                      {podeEscolherTecnico ? TEXTO_ENVIAR_ANALISE : "Registrar Laudo"}
                    </Button>
                    {podeEscolherTecnico && (
                      <Button
                        variant="outline"
                        disabled={
                          createMutation.isPending ||
                          cartelaValida === false ||
                          pecasLoading
                        }
                        onClick={() => createMutation.mutate(true)}
                      >
                        {createMutation.isPending && createMutation.variables === true && (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        )}
                        {TEXTO_ENVIAR_TECNICO}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Etapa final: o botão acompanha as fotos salvas (mesmo cache da grade) e trava durante o envio. */
function EtapaFotosVeiculo({
  veiculoId,
  onClose,
}: {
  veiculoId: string;
  onClose: () => void;
}) {
  const { data } = useFotosVeiculo(veiculoId);
  const [enviando, setEnviando] = useState(false);
  const completo = !!data && posicoesFaltantes(data).length === 0;
  return (
    <>
      <div className="flex-1 overflow-y-auto min-h-0">
        <FotosVeiculo veiculoId={veiculoId} onEnviandoChange={setEnviando} />
      </div>
      <div className="flex justify-end pt-2 border-t shrink-0">
        <Button
          variant={completo ? "default" : "outline"}
          disabled={enviando}
          onClick={onClose}
        >
          {completo ? "Concluir" : "Fazer depois"}
        </Button>
      </div>
    </>
  );
}

// Helper: gera etiqueta da peça (UF + tipo_digit + cartela + seq)
function buildEtiqueta(
  peca: Peca,
  veiculo: Veiculo,
  cartela: string,
  uf: string,
): string {
  const tipoDigito: Record<string, string> = {
    auto_leve: "1",
    moto: "2",
    caminhao: "3",
    onibus: "4",
  };
  const digito = tipoDigito[veiculo.tipo] ?? "1";
  const cartelaPadded = cartela.padStart(10, "0");
  const seq = (peca.ufSequencias as Record<string, number>)?.[uf] ?? 0;
  const seqPadded = String(seq).padStart(3, "0");
  return `${uf}${digito}${cartelaPadded}${seqPadded}`;
}
